import {
  ArrowRight,
  Check,
  ChevronLeft,
  Copy,
  ExternalLink,
  FlaskConical,
  ListChecks,
  Loader2,
  LockKeyhole,
  MessageSquare,
  Pencil,
  Play,
  Power,
  PowerOff,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { JsonTable } from "~/components/json-table";
import { Badge, Button, Card, Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui";
import type {
  CategoryDetailUI,
  CategorySchemaMigration,
  CategorySchemaMigrationRunAction,
  PropertySchemaRow,
  RelationTypeRow,
} from "~/features/admin/categories/types";
import type { DipPipelineDryRunResultTarget } from "~/features/dataImportProjects";
import { cn } from "~/lib/utils";
import { v2Route } from "~/v2/routes";

interface CategoryDetailPresenterProps {
  category: CategoryDetailUI;
  onBack: () => void;
  onEdit: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  processing: boolean;
  schemaMigrations?: CategorySchemaMigration[];
  schemaMigrationsLoading?: boolean;
  schemaMigrationsError?: boolean;
  schemaMigrationsHasNextPage?: boolean;
  schemaMigrationsLoadingMore?: boolean;
  onLoadMoreSchemaMigrations?: () => void;
  onRunSchemaMigration?: (action: CategorySchemaMigrationRunAction, planKey: string, jobCount: number) => void;
  schemaMigrationDryRunResults?: ReadonlyMap<string, DipPipelineDryRunResultTarget>;
  onOpenSchemaMigrationDryRunResult?: (result: DipPipelineDryRunResultTarget) => void;
  runningSchemaMigrationPlanKey?: string | null;
  runningSchemaMigrationAction?: CategorySchemaMigrationRunAction | null;
}

// Backend deferred-change reason -> human-readable i18n key. Keep the fallback so a newly added
// backend reason remains visible until Dewey adds its translation.
const DEFERRED_CHANGE_REASON_KEY: Record<string, string> = {
  "type-conversion-not-implemented":
    "feature.categories.recordMigration.deferredChangeReasons.typeConversionNotImplemented",
  "rename-with-type-change": "feature.categories.recordMigration.deferredChangeReasons.renameWithTypeChange",
  "rename-with-narrowed-validation":
    "feature.categories.recordMigration.deferredChangeReasons.renameWithNarrowedValidation",
  "validation-narrowed": "feature.categories.recordMigration.deferredChangeReasons.validationNarrowed",
  "rename-set-exceeds-request-cap":
    "feature.categories.recordMigration.deferredChangeReasons.renameSetExceedsRequestCap",
  "retype-set-exceeds-request-cap":
    "feature.categories.recordMigration.deferredChangeReasons.retypeSetExceedsRequestCap",
};

export function CategoryDetailPresenter({
  category,
  onBack,
  onEdit,
  onActivate,
  onDeactivate,
  processing,
  schemaMigrations = [],
  schemaMigrationsLoading = false,
  schemaMigrationsError = false,
  schemaMigrationsHasNextPage = false,
  schemaMigrationsLoadingMore = false,
  onLoadMoreSchemaMigrations,
  onRunSchemaMigration,
  schemaMigrationDryRunResults = new Map(),
  onOpenSchemaMigrationDryRunResult,
  runningSchemaMigrationPlanKey = null,
  runningSchemaMigrationAction = null,
}: CategoryDetailPresenterProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <Button type="button" variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ChevronLeft />
        <span>{t("ui.backToList")}</span>
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-dewey-title">{category.name}</h1>
            {category.isDeactivated && <Badge tone="default">{t("ui.deactivated")}</Badge>}
          </div>
          {category.displayName !== category.name && (
            <div className="mt-1 text-14-regular text-fg-2">{category.displayName}</div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={v2Route.changeRequests.$buildPath({ searchParams: { categoryId: category.id } })}>
              <ListChecks />
              <span>{t("feature.categories.recordMigration.changeRequests")}</span>
            </Link>
          </Button>
          {category.documentUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={category.documentUrl} target="_blank" rel="noreferrer">
                <ExternalLink />
                <span>{t("ui.document")}</span>
              </a>
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil />
            <span>{t("ui.edit")}</span>
          </Button>
          {category.isDeactivated ? (
            <Button type="button" variant="ghost" size="sm" onClick={onActivate} disabled={processing}>
              <Power />
              <span>{t("ui.activate")}</span>
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={onDeactivate} disabled={processing}>
              <PowerOff />
              <span>{t("ui.deactivate")}</span>
            </Button>
          )}
        </div>
      </div>

      <SubHeader category={category} />

      <PropertiesTable category={category} />

      <PropertySchemasSection schemas={category.propertySchemas} />

      <RecordMigrationsSection
        migrations={schemaMigrations}
        loading={schemaMigrationsLoading}
        error={schemaMigrationsError}
        hasNextPage={schemaMigrationsHasNextPage}
        loadingMore={schemaMigrationsLoadingMore}
        onLoadMore={onLoadMoreSchemaMigrations}
        onRun={onRunSchemaMigration}
        dryRunResults={schemaMigrationDryRunResults}
        onOpenDryRunResult={onOpenSchemaMigrationDryRunResult}
        runningPlanKey={runningSchemaMigrationPlanKey}
        runningAction={runningSchemaMigrationAction}
      />

      {category.typeId === "entity" && (
        <RelationsSection
          currentCategoryName={category.name}
          sourceRelations={category.sourceRelations}
          targetRelations={category.targetRelations}
        />
      )}
    </div>
  );
}

interface RecordMigrationsSectionProps {
  migrations: CategorySchemaMigration[];
  loading: boolean;
  error: boolean;
  hasNextPage: boolean;
  loadingMore: boolean;
  onLoadMore?: () => void;
  onRun?: (action: CategorySchemaMigrationRunAction, planKey: string, jobCount: number) => void;
  dryRunResults: ReadonlyMap<string, DipPipelineDryRunResultTarget>;
  onOpenDryRunResult?: (result: DipPipelineDryRunResultTarget) => void;
  runningPlanKey: string | null;
  runningAction: CategorySchemaMigrationRunAction | null;
}

function RecordMigrationsSection({
  migrations,
  loading,
  error,
  hasNextPage,
  loadingMore,
  onLoadMore,
  onRun,
  dryRunResults,
  onOpenDryRunResult,
  runningPlanKey,
  runningAction,
}: RecordMigrationsSectionProps) {
  const { t } = useTranslation();

  return (
    <section className="space-y-2">
      <h2 className="text-16-bold leading-tight">{t("feature.categories.recordMigration.title")}</h2>
      {loading && migrations.length === 0 ? (
        <Card>
          <div className="py-2 text-14-regular text-fg-3">{t("ui.loading")}</div>
        </Card>
      ) : error && migrations.length === 0 ? (
        <Card>
          <div className="py-2 text-14-regular text-danger">{t("feature.categories.recordMigration.loadFailed")}</div>
        </Card>
      ) : migrations.length === 0 ? (
        <Card>
          <div className="py-2 text-14-regular text-fg-3">{t("feature.categories.recordMigration.empty")}</div>
        </Card>
      ) : (
        <div className="space-y-3">
          {migrations.map((migration) => {
            const batchEvidencesJobIds = [...new Set(migration.pages.flatMap((page) => page.batchEvidencesJobIds))];
            const batchChangeRequestsJobIds = [
              ...new Set(migration.pages.flatMap((page) => page.batchChangeRequestsJobIds)),
            ];
            const jobCount = batchEvidencesJobIds.length + batchChangeRequestsJobIds.length;
            const canRun = migration.completedAt === null && migration.runnable && onRun;
            const isRunning = runningPlanKey === migration.planKey;
            const dryRunResult = dryRunResults.get(migration.planKey);

            return (
              <Card key={migration.planKey} className="space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-14-bold text-fg-1">{migration.schemaVersion}</span>
                      <Badge tone={migration.completedAt ? "status-approved" : "status-processing"}>
                        {t(
                          migration.completedAt
                            ? "feature.categories.recordMigration.migrationCompleted"
                            : migration.runnable
                              ? "feature.categories.recordMigration.migrationReady"
                              : "feature.categories.recordMigration.migrationInProgress",
                        )}
                      </Badge>
                    </div>
                    <div className="mt-1 font-mono text-12-regular text-fg-3">{migration.planKey}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {migration.completedAt && (
                      <div className="text-12-regular tabular-nums text-fg-3">{formatDate(migration.completedAt)}</div>
                    )}
                    {dryRunResult && onOpenDryRunResult ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={runningPlanKey !== null}
                        onClick={() => onOpenDryRunResult(dryRunResult)}
                      >
                        <FlaskConical />
                        {t("feature.categories.recordMigration.openDryRunResult")}
                      </Button>
                    ) : null}
                    {canRun ? (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={runningPlanKey !== null}
                          onClick={() => onRun("dryrun", migration.planKey, jobCount)}
                        >
                          {isRunning && runningAction === "dryrun" ? (
                            <Loader2 className="animate-spin" />
                          ) : (
                            <FlaskConical />
                          )}
                          {t(
                            isRunning && runningAction === "dryrun"
                              ? "feature.categories.recordMigration.dryRunRunning"
                              : "feature.categories.recordMigration.dryRun",
                          )}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={runningPlanKey !== null}
                          onClick={() => onRun("run", migration.planKey, jobCount)}
                        >
                          {isRunning && runningAction === "run" ? <Loader2 className="animate-spin" /> : <Play />}
                          {t(
                            isRunning && runningAction === "run"
                              ? "feature.categories.recordMigration.runStarting"
                              : "feature.categories.recordMigration.run",
                          )}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="overflow-x-auto rounded-[var(--radius-sm)] border border-border-default">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-bg-subtle">
                        <Th>{t("feature.categories.recordMigration.dataImportProject")}</Th>
                        <Th>{t("feature.categories.recordMigration.status")}</Th>
                        <Th>{t("feature.categories.recordMigration.jobs")}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {migration.pages.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-3.5 py-3 text-14-regular text-fg-3">
                            {t(
                              migration.completedAt
                                ? "feature.categories.recordMigration.noAutomaticMigrationTargets"
                                : "feature.categories.recordMigration.pagesPending",
                            )}
                          </td>
                        </tr>
                      ) : (
                        migration.pages.map((page) => (
                          <tr key={page.dataImportProjectId} className="border-b border-border-default last:border-b-0">
                            <Td>
                              {page.dataImportProjectStatus ? (
                                <Link
                                  to={v2Route.dataImportProjects.detail.$buildPath({
                                    params: { id: page.dataImportProjectId },
                                  })}
                                  className="font-mono text-12-regular text-primary-700 hover:underline"
                                >
                                  {page.dataImportProjectId}
                                </Link>
                              ) : (
                                <span className="font-mono text-12-regular text-fg-2">{page.dataImportProjectId}</span>
                              )}
                            </Td>
                            <Td>
                              {page.dataImportProjectStatus ? (
                                <Badge tone={page.dataImportProjectStatus === "Done" ? "status-approved" : "default"}>
                                  {page.dataImportProjectStatus}
                                </Badge>
                              ) : (
                                <Badge tone="status-processing">
                                  {t("feature.categories.recordMigration.projectPending")}
                                </Badge>
                              )}
                            </Td>
                            <Td>
                              {page.batchEvidencesJobIds.length === 0 && page.batchChangeRequestsJobIds.length === 0 ? (
                                <span className="text-fg-3">—</span>
                              ) : (
                                <div className="space-y-1.5">
                                  {page.batchEvidencesJobIds.length > 0 ? (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-10-regular uppercase tracking-[0.06em] text-fg-3">BE</span>
                                      {page.batchEvidencesJobIds.map((jobId) => (
                                        <Link
                                          key={jobId}
                                          to={v2Route.dataImportProjects.detail.batchEvidencesJob.$buildPath({
                                            params: { id: page.dataImportProjectId, jobId },
                                          })}
                                          className="font-mono text-12-regular text-primary-700 hover:underline"
                                        >
                                          {jobId}
                                        </Link>
                                      ))}
                                    </div>
                                  ) : null}
                                  {page.batchChangeRequestsJobIds.length > 0 ? (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-10-regular uppercase tracking-[0.06em] text-fg-3">BCR</span>
                                      {page.batchChangeRequestsJobIds.map((jobId) => (
                                        <Link
                                          key={jobId}
                                          to={v2Route.dataImportProjects.detail.batchChangeRequestsJob.$buildPath({
                                            params: { id: page.dataImportProjectId, jobId },
                                          })}
                                          className="font-mono text-12-regular text-primary-700 hover:underline"
                                        >
                                          {jobId}
                                        </Link>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </Td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {migration.deferredChanges.length > 0 && (
                  <div className="rounded-[var(--radius-sm)] bg-bg-subtle px-3 py-2">
                    <div className="text-12-medium text-fg-2">
                      {t("feature.categories.recordMigration.deferredChanges")}
                    </div>
                    <div className="mt-1 text-12-regular text-fg-3">
                      {t("feature.categories.recordMigration.deferredChangesDescription")}
                    </div>
                    <ul className="mt-1.5 space-y-1 text-12-regular text-fg-2">
                      {migration.deferredChanges.map((change) => (
                        <li key={`${change.oldName}-${change.newName}-${change.reason}`}>
                          <span className="font-mono text-fg-1">
                            {change.oldName} → {change.newName}
                          </span>
                          <span className="ml-2 text-fg-3">
                            {DEFERRED_CHANGE_REASON_KEY[change.reason]
                              ? t(DEFERRED_CHANGE_REASON_KEY[change.reason])
                              : change.reason}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
      {error && migrations.length > 0 && (
        <Card>
          <div role="alert" className="py-2 text-14-regular text-danger">
            {t("feature.categories.recordMigration.loadFailed")}
          </div>
        </Card>
      )}
      {hasNextPage && (
        <div className="flex justify-center pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onLoadMore} disabled={loadingMore}>
            {t(loadingMore ? "ui.loading" : "feature.categories.recordMigration.loadMore")}
          </Button>
        </div>
      )}
    </section>
  );
}

function SubHeader({ category }: { category: CategoryDetailUI }) {
  const { t } = useTranslation();
  const items: { label: string; value: React.ReactNode }[] = [
    { label: t("model.common.id"), value: <CopyableText text={category.id} className="font-mono" /> },
  ];
  if (category.createdAt) {
    items.push({ label: t("model.common.createdAt"), value: formatDate(category.createdAt) });
  }
  if (category.updatedAt && category.updatedAt !== category.createdAt) {
    items.push({ label: t("model.common.updatedAt"), value: formatDate(category.updatedAt) });
  }
  if (category.deactivatedAt) {
    items.push({ label: t("model.common.deactivatedAt"), value: formatDate(category.deactivatedAt) });
  }
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-12-regular text-fg-3">
      {items.map((item, i) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          {i > 0 && (
            <span aria-hidden className="text-border-strong">
              |
            </span>
          )}
          <span className="font-medium uppercase tracking-[0.04em]">{item.label}</span>
          <span className="text-fg-2 tabular-nums">{item.value}</span>
        </span>
      ))}
    </div>
  );
}

function CopyableText({ text, className }: { text: string; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await copyToClipboard(text);
      setCopied(true);
      toast.success(t("feature.common.idCopied"));
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={className}>{text}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex h-5 w-5 items-center justify-center rounded-[var(--radius-xs)] text-fg-3 hover:bg-bg-subtle hover:text-fg-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
            aria-label={t("ui.copyId")}
          >
            {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
          </button>
        </TooltipTrigger>
        <TooltipContent>{t("ui.copyId")}</TooltipContent>
      </Tooltip>
    </span>
  );
}

function PropertiesTable({ category }: { category: CategoryDetailUI }) {
  const { t } = useTranslation();
  const rows: { label: string; value: React.ReactNode }[] = [
    { label: t("model.category.type"), value: <Badge tone="default">{category.typeId}</Badge> },
    {
      label: t("model.category.path"),
      value: category.categoryPath ? <span className="font-mono">{category.categoryPath}</span> : "—",
    },
    { label: t("model.common.name"), value: category.name },
    { label: t("model.category.label.ja"), value: category.jaLabel ?? "—" },
    { label: t("model.category.label.en"), value: category.enLabel ?? "—" },
    { label: t("model.common.description"), value: category.description || "—" },
  ];
  if (category.relationType) {
    rows.push({
      label: t("model.relationType.modelName"),
      value: <RelationTypeCard relationType={category.relationType} />,
    });
  }
  return (
    <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
      <table className="w-full border-collapse">
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.label} className={i < rows.length - 1 ? "border-b border-border-default" : undefined}>
              <th className="w-44 bg-bg-subtle px-3.5 py-2 text-left text-12-medium text-fg-2 align-top">
                {row.label}
              </th>
              <td className="px-3.5 py-2 text-14-regular leading-[1.6] text-fg-1">{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface PropertySchemasSectionProps {
  schemas: PropertySchemaRow[];
}

function PropertySchemasSection({ schemas }: PropertySchemasSectionProps) {
  const { t } = useTranslation();
  return (
    <section className="space-y-2">
      <h2 className="text-16-bold leading-tight capitalize">{t("model.propertySchema.modelNames")}</h2>
      {schemas.length === 0 ? (
        <Card>
          <div className="py-2 text-14-regular text-fg-3">—</div>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-bg-subtle">
                <Th className="w-16 text-center">{t("model.common.position")}</Th>
                <Th>{t("model.common.name")}</Th>
                <Th>{t("model.propertySchema.propertyTypeId")}</Th>
                <Th className="text-center">{t("model.propertySchemaAssociation.required")}</Th>
                <Th className="text-center">{t("model.propertySchemaAssociation.unique")}</Th>
                <Th>{t("model.propertySchemaAssociation.propertyAttributeTypeId")}</Th>
                <Th>{t("ui.options")}</Th>
                <Th className="w-12">{t("model.common.description")}</Th>
              </tr>
            </thead>
            <tbody>
              {schemas.map((s) => (
                <tr
                  key={s.id}
                  className={cn("border-b border-border-default last:border-b-0", !s.isOwn && "bg-bg-subtle")}
                >
                  <Td className="text-center tabular-nums text-fg-2">{s.position}</Td>
                  <Td className="font-medium text-fg-1">
                    <div>{s.name}</div>
                    {!s.isOwn && (
                      <div className="mt-1 flex items-center gap-1 text-12-regular text-fg-3">
                        <span role="img" aria-label={t("feature.categories.inheritance.locked")}>
                          <LockKeyhole aria-hidden className="h-3 w-3" />
                        </span>
                        <Link
                          to={v2Route.admin.categories.detail.$buildPath({ params: { id: s.sourceCategoryId } })}
                          className="text-primary-700 hover:underline"
                        >
                          {t("feature.categories.inheritance.inheritedFrom", { category: s.sourceCategoryName })}
                        </Link>
                      </div>
                    )}
                  </Td>
                  <Td>
                    <Badge tone="default">{s.propertyTypeId}</Badge>
                  </Td>
                  <Td className="text-center">
                    {s.required && <Check className="mx-auto h-3.5 w-3.5 text-success" />}
                  </Td>
                  <Td className="text-center">{s.unique && <Check className="mx-auto h-3.5 w-3.5 text-success" />}</Td>
                  <Td>{s.propertyAttributeTypeId ? <Badge tone="default">{s.propertyAttributeTypeId}</Badge> : "—"}</Td>
                  <Td>
                    <SchemaOptions schema={s} />
                  </Td>
                  <Td className="text-center">
                    {s.description ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-xs)] text-fg-3 hover:bg-bg-subtle hover:text-fg-1"
                            aria-label={s.description}
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-xs">
                          {s.description}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-fg-3">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SchemaOptions({ schema }: { schema: PropertySchemaRow }) {
  const { propertyTypeId, selectOptions, urlAllowList } = schema;
  if (propertyTypeId === "select" || propertyTypeId === "multiSelect") {
    if (!selectOptions || selectOptions.length === 0) return <span className="text-fg-3">—</span>;
    return <JsonTable target={selectOptions} />;
  }
  if (propertyTypeId === "url") {
    if (!urlAllowList || urlAllowList.length === 0) return <span className="text-fg-3">—</span>;
    return (
      <ul className="flex flex-col gap-0.5 font-mono text-12-regular">
        {urlAllowList.map((item, i) => (
          <li key={`${item.value}-${item.regex}-${i}`} className="truncate">
            {item.regex ? `regex: ${item.regex}` : item.value}
          </li>
        ))}
      </ul>
    );
  }
  return <span className="text-fg-3">—</span>;
}

function RelationsSection({
  currentCategoryName,
  sourceRelations,
  targetRelations,
}: {
  currentCategoryName: string;
  sourceRelations: RelationTypeRow[];
  targetRelations: RelationTypeRow[];
}) {
  const { t } = useTranslation();
  return (
    <section className="space-y-2">
      <h2 className="text-16-bold leading-tight capitalize">{t("model.record.relation.modelNames")}</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <RelationsCard
          title={t("model.category.relation.sourceRelations")}
          rows={sourceRelations}
          currentCategoryName={currentCategoryName}
          /* sourceRelations: this category IS the source */
          mode="source"
        />
        <RelationsCard
          title={t("model.category.relation.targetRelations")}
          rows={targetRelations}
          currentCategoryName={currentCategoryName}
          /* targetRelations: this category IS the target */
          mode="target"
        />
      </div>
    </section>
  );
}

function RelationsCard({
  title,
  rows,
  currentCategoryName,
  mode,
}: {
  title: string;
  rows: RelationTypeRow[];
  currentCategoryName: string;
  mode: "source" | "target";
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-[var(--radius-md)] border border-border-default bg-white">
      <div className="border-b border-border-default px-3.5 py-2.5 text-14-bold capitalize text-fg-1">{title}</div>
      {rows.length === 0 ? (
        <div className="px-3.5 py-3 text-14-regular text-fg-3">—</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-bg-subtle">
                <Th>{t("model.relationType.sourceCategory")}</Th>
                <Th>{t("model.relationType.modelName")}</Th>
                <Th>{t("model.relationType.targetCategory")}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.relationType.id} className="border-b border-border-default last:border-b-0">
                  {mode === "source" ? (
                    <>
                      <Td className="font-medium text-fg-1">{currentCategoryName}</Td>
                      <Td>
                        <LinkedLabel to={`/admin/relation-types/${r.relationType.id}`} label={r.relationType.name} />
                      </Td>
                      <Td>
                        <LinkedLabel to={`/admin/categories/${r.otherCategory.id}`} label={r.otherCategory.label} />
                      </Td>
                    </>
                  ) : (
                    <>
                      <Td>
                        <LinkedLabel to={`/admin/categories/${r.otherCategory.id}`} label={r.otherCategory.label} />
                      </Td>
                      <Td>
                        <LinkedLabel to={`/admin/relation-types/${r.relationType.id}`} label={r.relationType.name} />
                      </Td>
                      <Td className="font-medium text-fg-1">{currentCategoryName}</Td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** V1's LinkInternal pattern: a small arrow icon that's the actual link, with plain text next to it. */
function LinkedLabel({ to, label, mono = false }: { to: string; label: string; mono?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Link
        to={to}
        className="inline-flex h-5 w-5 items-center justify-center rounded-[var(--radius-xs)] text-primary-600 hover:bg-primary-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
        aria-label={label}
      >
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
      <span className={mono ? "font-mono text-12-regular text-fg-1" : "text-fg-1"}>{label}</span>
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={`border-b border-border-default px-3.5 py-2.5 text-left text-10-regular uppercase tracking-[0.06em] text-fg-2 ${className ?? ""}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3.5 py-2 text-14-regular align-middle text-fg-1 ${className ?? ""}`}>{children}</td>;
}

function RelationTypeCard({ relationType }: { relationType: NonNullable<CategoryDetailUI["relationType"]> }) {
  const { t } = useTranslation();
  return (
    <div className="inline-flex w-full max-w-md flex-col gap-1 rounded-[var(--radius-md)] border border-border-default bg-bg-subtle px-3 py-2">
      <MiniRow label={t("model.common.id")}>
        <LinkedLabel to={`/admin/relation-types/${relationType.id}`} label={relationType.id} mono />
      </MiniRow>
      <MiniRow label={t("model.common.name")}>{relationType.name}</MiniRow>
      <MiniRow label={t("model.relationType.sourceCategory")}>
        <LinkedLabel to={`/admin/categories/${relationType.source.id}`} label={relationType.source.label} />
      </MiniRow>
      <MiniRow label={t("model.relationType.targetCategory")}>
        <LinkedLabel to={`/admin/categories/${relationType.target.id}`} label={relationType.target.label} />
      </MiniRow>
    </div>
  );
}

function MiniRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 text-14-regular">
      <span className="w-32 shrink-0 text-10-regular uppercase tracking-[0.04em] text-fg-3">{label}</span>
      <span className="min-w-0 truncate text-fg-1">{children}</span>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().slice(0, 16).replace("T", " ");
  } catch {
    return iso;
  }
}
